<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'

import {
  MODE_LABELS,
  listLaneOptions,
  siteLabel,
  type LaneMode,
  type LaneRow,
} from '../api/lanes'
import { listSiteOptions, type SiteRow } from '../api/sites'
import {
  SCENARIOS,
  SCENARIO_LABELS,
  SCENARIO_TAG,
  STATUS_LABELS,
  STATUS_TAG,
  canAct,
  createSimulation,
  deleteSimulation,
  formFromSimulation,
  getSimulation,
  listSimulations,
  parseResult,
  resultError,
  resultScenario,
  runSimulation,
  simulationFormError,
  simulationInput,
  updateSimulation,
  type Simulation,
  type SimulationForm,
  type SimulationRow,
  type SimulationScenario,
  type SimulationStatus,
} from '../api/simulations'

const route = useRoute()

const rows = ref<SimulationRow[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
/** 删除失败（running 的任务后端 409）单独一个 ref，与列表错误分开显示 */
const refError = ref<unknown>(null)
const notice = ref('')

const query = reactive({ keyword: '', scenario: '', status: '', page: 1, size: 20 })

/** 参数表单的下拉参照：网点 / 路径各一份 */
const sites = ref<SiteRow[]>([])
const lanes = ref<LaneRow[]>([])
const refNotice = ref('')

const formOpen = ref(false)
const editing = ref<SimulationRow | null>(null)
const saving = ref(false)
const formError = ref<unknown>(null)
const form = reactive<SimulationForm>({
  name: '',
  scenario: 'lane_optimize',
  site_ids: [],
  lanes: [],
  candidate_lane_ids: [],
  demand_factor: undefined,
  city_demand: [],
  remark: '',
})

/** 详情 / 结果弹窗 */
const detailOpen = ref(false)
const detailLoading = ref(false)
const detailError = ref<unknown>(null)
const detail = ref<Simulation | null>(null)
/** 运行的失败：非法流转是 409，与加载失败分开显示，都带 trace_id */
const runError = ref<unknown>(null)
const running = ref(false)

const formErrorText = computed(() => simulationFormError(form))

/** 三种场景各自能填哪些参数（与 algo.go 的算法口径一一对应） */
const usesSites = computed(() => form.scenario !== 'cost_compare')
const usesLanes = computed(() => form.scenario !== 'site_layout')
const usesCandidates = computed(() => form.scenario === 'cost_compare')
const usesCityDemand = computed(() => form.scenario === 'site_layout')

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listSimulations({
      page: query.page,
      size: query.size,
      keyword: query.keyword,
      scenario: query.scenario,
      status: query.status,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

async function loadReference(): Promise<void> {
  refNotice.value = ''
  try {
    const [siteList, laneList] = await Promise.all([listSiteOptions(), listLaneOptions()])
    sites.value = siteList
    lanes.value = laneList
  } catch {
    // 参照只影响参数下拉，列表与详情照常可用
    refNotice.value = '网点 / 路径参照加载失败：参数只能按默认口径跑（全量启用网点 / 全量启用路径）。'
    sites.value = []
    lanes.value = []
  }
}

onMounted(() => {
  // 工作台的 KPI 卡片是带状态钻取过来的（/simulations?status=draft）
  const status = String(route.query.status ?? '')
  if (status) query.status = status
  const scenario = String(route.query.scenario ?? '')
  if (scenario) query.scenario = scenario
  void load()
  void loadReference()
})

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

function resetForm(): void {
  Object.assign(form, {
    name: '',
    scenario: 'lane_optimize' as SimulationForm['scenario'],
    site_ids: [],
    lanes: [],
    candidate_lane_ids: [],
    demand_factor: undefined,
    city_demand: [],
    remark: '',
  })
}

function openCreate(): void {
  editing.value = null
  resetForm()
  formError.value = null
  formOpen.value = true
}

function openEdit(row: SimulationRow): void {
  editing.value = row
  // 编辑权限码是 nos:sim:create（spec §6.2 没有 sim:update），只有 draft 能改。
  // 回显以 params 里的 id 为准，参照列表只用来补齐没被点名的可选项。
  const siteIds = sites.value.map((s) => s.id)
  const laneIds = lanes.value.map((l) => l.id)
  Object.assign(form, formFromSimulation(row, siteIds, laneIds, laneIds))
  formError.value = null
  formOpen.value = true
}

async function save(): Promise<void> {
  if (formErrorText.value) return
  saving.value = true
  formError.value = null
  const input = simulationInput({ ...form })
  try {
    if (editing.value) await updateSimulation(editing.value.id, input)
    else await createSimulation(input)
    formOpen.value = false
    await load(editing.value ? `仿真任务已更新` : `仿真任务已创建（待运行）`)
  } catch (e) {
    // params 有未知字段 / 取值非法（422）、非 draft 编辑（409）都原样把 trace_id 露出来
    formError.value = e
  } finally {
    saving.value = false
  }
}

async function remove(row: SimulationRow): Promise<void> {
  notice.value = ''
  error.value = null
  refError.value = null
  try {
    await deleteSimulation(row.id)
    await load(`已删除仿真任务 ${row.doc_no}`)
  } catch (e) {
    // running 的任务后端 409（算法还在跑，删掉结果无处落地）
    refError.value = e
  }
}

/** 运行：draft / done 才放行；算法跑挂了后端仍返 200（任务自己落到 failed） */
async function run(row: SimulationRow): Promise<void> {
  notice.value = ''
  error.value = null
  runError.value = null
  running.value = true
  try {
    const updated = await runSimulation(row.id)
    if (updated.status === 'failed') {
      await load(`仿真任务 ${row.doc_no} 运行失败：${resultError(parseResult(updated.result))}`)
    } else {
      await load(`仿真任务 ${row.doc_no} 运行完成`)
    }
    // 详情开着就同步刷新，让结果卡片直接跟上
    if (detail.value && detail.value.id === updated.id) {
      detail.value = updated
    } else if (detailOpen.value) {
      await openDetail(row)
    }
  } catch (e) {
    runError.value = e
  } finally {
    running.value = false
  }
}

async function openDetail(row: SimulationRow): Promise<void> {
  detail.value = null
  detailError.value = null
  runError.value = null
  detailLoading.value = true
  detailOpen.value = true
  try {
    detail.value = await getSimulation(row.id)
  } catch (e) {
    detailError.value = e
  } finally {
    detailLoading.value = false
  }
}

/** ---------- 结果卡片（按场景分派）---------- */

/** result 是 JSON 文本：解析失败 / 未运行都返回 null，页面按「未运行」渲染，绝不崩 */
const result = computed(() => parseResult(detail.value?.result))
const resultFailed = computed(() => resultError(result.value))
const scenario = computed(() => resultScenario(result.value))

/**
 * 分派到具体的结果类型：三个场景字段完全不同（spec §6.1），不能只显示一个总数。
 * 用「字段在不在」而不是只看 scenario：库里被手改过的 result 可能 scenario 对、字段缺，
 * 那样三张卡片都不落，最后统一走「无法解析 + 原始文本」的兜底，不会出现空白面板。
 */
const mst = computed(() => {
  const r = result.value
  return scenario.value === 'lane_optimize' && r && 'total_km' in r ? r : null
})
const cost = computed(() => {
  const r = result.value
  return scenario.value === 'cost_compare' && r && 'saving' in r ? r : null
})
const layout = computed(() => {
  const r = result.value
  return scenario.value === 'site_layout' && r && 'cities' in r ? r : null
})

/** params 文本原样展示（服务端归一后的紧凑串） */
const paramsText = computed(() => detail.value?.params?.trim() || '{}')

/** saving 为正 = 更便宜，为负 = 要多花钱 */
const savingText = computed(() => {
  const c = cost.value
  if (!c) return ''
  const pct = (c.saving_ratio * 100).toFixed(2)
  return c.saving >= 0 ? `节省 ¥${c.saving}（${pct}%）` : `增加 ¥${-c.saving}（${pct}%）`
})

function addCityDemand(): void {
  form.city_demand.push({ city: '', demand: undefined })
}

function removeCityDemand(index: number): void {
  form.city_demand.splice(index, 1)
}

function modeLabel(mode: string): string {
  return MODE_LABELS[mode as LaneMode] ?? mode
}

function percentText(ratio: number | undefined): string {
  if (typeof ratio !== 'number' || Number.isNaN(ratio)) return '—'
  return `${(ratio * 100).toFixed(2)}%`
}

/** 城市 / 瓶颈表里的网点名：结果里带了 site_code / site_name，优先用；没有再退回参照 */
function siteName(code: string, name: string): string {
  const hit = sites.value.find((s) => s.code === code)
  return [hit ? `${hit.code} ${hit.name}`.trim() : `${code} ${name}`.trim()].filter(Boolean).join(' ')
}
</script>

<template>
  <div class="yc-sim">
    <header class="yc-sim__bar">
      <el-input
        v-model="query.keyword"
        class="yc-sim__keyword"
        placeholder="单号 / 名称"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select v-model="query.scenario" class="yc-sim__ref" placeholder="全部场景" clearable @change="search">
        <el-option v-for="s in SCENARIOS" :key="s" :label="SCENARIO_LABELS[s]" :value="s" />
      </el-select>
      <el-select v-model="query.status" class="yc-sim__ref" placeholder="全部状态" clearable @change="search">
        <el-option v-for="(label, value) in STATUS_LABELS" :key="value" :label="label" :value="value" />
      </el-select>
      <el-button @click="search">查询</el-button>
      <el-button v-perm="'nos:sim:create'" type="primary" @click="openCreate">新建仿真任务</el-button>
      <el-button @click="load()">刷新</el-button>
      <span v-if="notice" class="yc-sim__notice">{{ notice }}</span>
    </header>

    <div v-if="error || refError" class="yc-sim__error">
      <TraceIdAlert :error="error ?? refError" />
    </div>
    <p v-else-if="refNotice" class="yc-sim__hint">{{ refNotice }}</p>

    <el-card v-loading="loading" class="yc-sim__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column prop="doc_no" label="单号" min-width="140" />
        <el-table-column prop="name" label="名称" min-width="180" show-overflow-tooltip />
        <el-table-column label="场景" width="170">
          <template #default="{ row }">
            <el-tag :type="SCENARIO_TAG[row.scenario as SimulationScenario] ?? 'info'" size="small">
              {{ SCENARIO_LABELS[row.scenario as SimulationScenario] ?? row.scenario }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="STATUS_TAG[row.status as SimulationStatus] ?? 'info'" size="small">
              {{ STATUS_LABELS[row.status as SimulationStatus] ?? row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="完成时间" min-width="155">
          <template #default="{ row }">{{ formatDateTime(row.finished_at) }}</template>
        </el-table-column>
        <el-table-column label="创建时间" min-width="155">
          <template #default="{ row }">{{ formatDateTime(row.created_at) }}</template>
        </el-table-column>
        <el-table-column label="耗时" width="130" align="right">
          <template #default="{ row }">
            {{ row.finished_at ? `${Math.max(0, Math.round((new Date(row.finished_at).getTime() - new Date(row.created_at).getTime()) / 1000))} 秒` : '—' }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="210" fixed="right">
          <template #default="{ row }">
            <el-button link type="primary" @click="openDetail(row)">详情</el-button>
            <el-button
              v-if="canAct(row.status as SimulationStatus, 'edit')"
              v-perm="'nos:sim:create'"
              link
              type="primary"
              @click="openEdit(row)"
            >
              编辑
            </el-button>
            <el-button
              v-if="canAct(row.status as SimulationStatus, 'run')"
              v-perm="'nos:sim:run'"
              link
              type="success"
              :loading="running"
              @click="run(row)"
            >
              运行
            </el-button>
            <el-popconfirm
              v-if="canAct(row.status as SimulationStatus, 'delete')"
              title="确认删除该仿真任务？"
              width="260"
              @confirm="remove(row)"
            >
              <template #reference>
                <el-button v-perm="'nos:sim:delete'" link type="danger">删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <EmptyState
        v-else-if="!loading"
        title="还没有仿真任务"
        description="先建网点与路径，再建一张仿真任务：选场景 → 填参数 → 运行，同参数可重跑复现。"
      >
        <el-button v-perm="'nos:sim:create'" type="primary" @click="openCreate">新建仿真任务</el-button>
      </EmptyState>
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-sim__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />

    <!-- 新建 / 编辑（编辑权限挂 nos:sim:create，spec §6.2 没有 sim:update；只有 draft 能改） -->
    <el-dialog
      v-if="formOpen"
      v-model="formOpen"
      :title="editing ? `编辑仿真任务 · ${editing.doc_no}` : '新建仿真任务'"
      width="680px"
      :close-on-click-modal="false"
    >
      <div v-if="formError" class="yc-sim__error">
        <TraceIdAlert :error="formError" />
      </div>

      <el-form label-position="top" @submit.prevent="save">
        <el-form-item label="名称" :error="form.name.trim() ? '' : '请输入仿真任务名称'">
          <el-input v-model="form.name" maxlength="128" placeholder="如 长三角路网优化（2026Q4）" />
        </el-form-item>

        <el-form-item label="场景">
          <el-select v-model="form.scenario" class="yc-sim__full">
            <el-option v-for="s in SCENARIOS" :key="s" :label="SCENARIO_LABELS[s]" :value="s" />
          </el-select>
          <p class="yc-sim__hint">
            {{
              form.scenario === 'lane_optimize'
                ? '对参与仿真的网点集合按 distance_km 求最小生成树（Prim），输出总里程 / 估算成本 / 边列表。'
                : form.scenario === 'cost_compare'
                  ? '对比「现网成本」与「启用候选路径后」的成本，saving 为正表示更便宜。'
                  : '按 city 分组统计能力与需求，给出覆盖率与瓶颈（单点负载 > 1 判 warn）。'
            }}
          </p>
        </el-form-item>

        <el-form-item label="需求系数（缺省 1.0，放大需求 / 成本估算；0 < x ≤ 100）">
          <el-input-number
            v-model="form.demand_factor"
            class="yc-sim__full"
            :min="0.01"
            :max="100"
            :precision="2"
            :step="0.1"
            placeholder="留空按 1.0"
            controls-position="right"
          />
        </el-form-item>

        <el-form-item v-if="usesSites" label="参与仿真的网点（留空 = 全部启用网点；点名了停用网点也照算）">
          <el-select
            v-model="form.site_ids"
            class="yc-sim__full"
            multiple
            filterable
            collapse-tags
            collapse-tags-tooltip
            placeholder="可留空"
          >
            <el-option
              v-for="s in sites"
              :key="s.id"
              :label="`${s.code} ${s.name}`.trim()"
              :value="s.id"
            />
          </el-select>
        </el-form-item>

        <el-form-item v-if="usesLanes" label="参与仿真的路径 / 现网口径（留空 = 全部启用路径）">
          <el-select
            v-model="form.lanes"
            class="yc-sim__full"
            multiple
            filterable
            collapse-tags
            collapse-tags-tooltip
            placeholder="可留空"
          >
            <el-option
              v-for="l in lanes"
              :key="l.id"
              :label="`${l.code} ${siteLabel(sites, l.from_site_id)}→${siteLabel(sites, l.to_site_id)}`"
              :value="l.id"
            />
          </el-select>
        </el-form-item>

        <el-form-item
          v-if="usesCandidates"
          label="候选路径（cost_compare：启用它们算「启用后」成本，通常选停用中的新通道）"
        >
          <el-select
            v-model="form.candidate_lane_ids"
            class="yc-sim__full"
            multiple
            filterable
            collapse-tags
            collapse-tags-tooltip
            placeholder="可留空（不启用任何候选）"
          >
            <el-option
              v-for="l in lanes"
              :key="l.id"
              :label="`${l.code} ${siteLabel(sites, l.from_site_id)}→${siteLabel(sites, l.to_site_id)}`"
              :value="l.id"
            />
          </el-select>
        </el-form-item>

        <template v-if="usesCityDemand">
          <el-form-item label="各城市日均件量（留空的城市按「需求系数 × 该城市网点平均能力」推算）">
            <div v-for="(row, i) in form.city_demand" :key="i" class="yc-sim__cityrow">
              <el-input v-model="row.city" maxlength="64" placeholder="城市，如 上海" />
              <el-input-number
                v-model="row.demand"
                class="yc-sim__citydemand"
                :min="0"
                :max="2147483647"
                :precision="2"
                :step="100"
                controls-position="right"
              />
              <el-button link type="danger" @click="removeCityDemand(i)">移除</el-button>
            </div>
            <el-button link type="primary" @click="addCityDemand">添加城市</el-button>
          </el-form-item>
        </template>

        <el-form-item label="备注">
          <el-input v-model="form.remark" maxlength="255" placeholder="可留空" />
        </el-form-item>

        <el-button type="primary" native-type="submit" :loading="saving" :disabled="!!formErrorText">
          {{ editing ? '保存' : '创建（待运行）' }}
        </el-button>
        <span v-if="formErrorText" class="yc-sim__hint yc-sim__hint--inline">{{ formErrorText }}</span>
      </el-form>

      <template #footer>
        <el-button @click="formOpen = false">取消</el-button>
      </template>
    </el-dialog>

    <!-- 详情 / 结果：三种场景的 result 字段完全不同，按场景分卡片 -->
    <el-dialog
      v-if="detailOpen"
      v-model="detailOpen"
      :title="detail ? `仿真详情 · ${detail.doc_no}` : '仿真详情'"
      width="900px"
      :close-on-click-modal="false"
    >
      <div v-if="detailError || runError" class="yc-sim__error">
        <TraceIdAlert :error="detailError ?? runError" />
      </div>

      <div v-loading="detailLoading">
        <template v-if="detail">
          <div class="yc-sim__facts">
            <div class="yc-sim__fact">
              <span>场景</span>{{ SCENARIO_LABELS[detail.scenario] }}
            </div>
            <div class="yc-sim__fact">
              <span>状态</span>
              <el-tag :type="STATUS_TAG[detail.status]" size="small">{{ STATUS_LABELS[detail.status] }}</el-tag>
            </div>
            <div class="yc-sim__fact"><span>完成时间</span>{{ formatDateTime(detail.finished_at) }}</div>
            <div class="yc-sim__fact"><span>创建时间</span>{{ formatDateTime(detail.created_at) }}</div>
            <div class="yc-sim__fact"><span>备注</span>{{ detail.remark || '—' }}</div>
          </div>

          <h3 class="yc-sim__sect">参数（params）</h3>
          <pre class="yc-sim__json">{{ paramsText }}</pre>

          <h3 class="yc-sim__sect">结果（result）</h3>
          <template v-if="!detail.result">
            <p class="yc-sim__hint">未运行。点「运行」后服务端跑一次确定性算法，同参数可重跑复现。</p>
          </template>
          <template v-else-if="resultFailed">
            <el-alert type="error" :closable="false" :title="`仿真失败：${resultFailed}`" />
          </template>

          <!-- lane_optimize：总里程 / 估算成本 / 边列表 -->
          <template v-else-if="mst">
            <div class="yc-sim__cards">
              <el-card class="yc-sim__card">
                <div class="yc-sim__value">{{ mst.total_km }} km</div>
                <div class="yc-sim__label">生成树总里程</div>
              </el-card>
              <el-card class="yc-sim__card">
                <div class="yc-sim__value">¥{{ mst.est_cost }}</div>
                <div class="yc-sim__label">估算成本（需求系数 {{ mst.demand_factor }}）</div>
              </el-card>
              <el-card class="yc-sim__card">
                <div class="yc-sim__value">{{ mst.site_count }} → {{ mst.edge_count }}</div>
                <div class="yc-sim__label">参与网点 / 选中边</div>
              </el-card>
            </div>
            <el-table :data="mst.edges" size="small" class="yc-sim__edges">
              <el-table-column prop="lane_code" label="路径" width="110" />
              <el-table-column label="方向" min-width="220" show-overflow-tooltip>
                <template #default="{ row }">
                  {{ siteLabel(sites, row.from_site_id) }} → {{ siteLabel(sites, row.to_site_id) }}
                </template>
              </el-table-column>
              <el-table-column label="方式" width="90">
                <template #default="{ row }">{{ modeLabel(row.mode) }}</template>
              </el-table-column>
              <el-table-column label="里程" width="100" align="right">
                <template #default="{ row }">{{ row.distance_km }} km</template>
              </el-table-column>
              <el-table-column label="每公里成本" width="110" align="right">
                <template #default="{ row }">¥{{ row.cost_per_km }}</template>
              </el-table-column>
              <el-table-column label="小计" width="110" align="right">
                <template #default="{ row }">¥{{ row.cost }}</template>
              </el-table-column>
            </el-table>
          </template>

          <!-- cost_compare：现网 vs 启用后 + saving -->
          <template v-else-if="cost">
            <div class="yc-sim__cards">
              <el-card class="yc-sim__card">
                <div class="yc-sim__value">¥{{ cost.current.total_cost }}</div>
                <div class="yc-sim__label">现网总成本（{{ cost.current.lane_count }} 条 / {{ cost.current.total_km }} km）</div>
              </el-card>
              <el-card class="yc-sim__card">
                <div class="yc-sim__value">¥{{ cost.proposed.total_cost }}</div>
                <div class="yc-sim__label">启用候选后（{{ cost.proposed.lane_count }} 条 / {{ cost.proposed.total_km }} km）</div>
              </el-card>
              <el-card class="yc-sim__card">
                <div class="yc-sim__value" :class="cost.saving >= 0 ? 'yc-sim__value--good' : 'yc-sim__value--bad'">
                  {{ savingText }}
                </div>
                <div class="yc-sim__label">节省 / 增加（需求系数 {{ cost.demand_factor }}）</div>
              </el-card>
            </div>
            <el-table :data="[cost.current, cost.proposed]" size="small" class="yc-sim__edges">
              <el-table-column label="口径" width="150">
                <template #default="{ $index }">{{ $index === 0 ? '现网' : '启用候选后' }}</template>
              </el-table-column>
              <el-table-column prop="lane_count" label="路径数" width="90" align="right" />
              <el-table-column prop="total_km" label="总里程（km）" min-width="120" align="right" />
              <el-table-column prop="total_cost" label="总成本" min-width="110" align="right" />
              <el-table-column label="每公里均价" min-width="120" align="right">
                <template #default="{ row }">¥{{ row.avg_cost_per_km }}</template>
              </el-table-column>
            </el-table>
            <p class="yc-sim__hint">
              候选路径：{{ cost.candidate_lane_ids.length ? cost.candidate_lane_ids.map((id) => `#${id}`).join('、') : '未启用任何候选' }}
            </p>
          </template>

          <!-- site_layout：按 city 的覆盖率 + 瓶颈 -->
          <template v-else-if="layout">
            <div class="yc-sim__cards">
              <el-card class="yc-sim__card">
                <div class="yc-sim__value">{{ percentText(layout.coverage_ratio) }}</div>
                <div class="yc-sim__label">总覆盖率（需求 {{ layout.total_demand }} / 能力 {{ layout.total_capacity }}）</div>
              </el-card>
              <el-card class="yc-sim__card">
                <div class="yc-sim__value">{{ layout.site_count }} / {{ layout.city_count }}</div>
                <div class="yc-sim__label">参与网点 / 城市数</div>
              </el-card>
              <el-card class="yc-sim__card">
                <div class="yc-sim__value" :class="layout.warn_count ? 'yc-sim__value--bad' : 'yc-sim__value--good'">
                  {{ layout.warn_count }}
                </div>
                <div class="yc-sim__label">瓶颈网点（负载 &gt; 1，需求系数 {{ layout.demand_factor }}）</div>
              </el-card>
            </div>

            <h4 class="yc-sim__subsect">按城市</h4>
            <el-table :data="layout.cities" size="small" class="yc-sim__edges">
              <el-table-column prop="city" label="城市" min-width="100" />
              <el-table-column prop="site_count" label="网点数" width="90" align="right" />
              <el-table-column prop="capacity" label="能力（件/日）" min-width="120" align="right" />
              <el-table-column prop="demand" label="需求（件/日）" min-width="120" align="right" />
              <el-table-column label="负载" width="90" align="right">
                <template #default="{ row }">{{ percentText(row.load_ratio) }}</template>
              </el-table-column>
              <el-table-column label="覆盖率" width="90" align="right">
                <template #default="{ row }">{{ percentText(row.coverage_ratio) }}</template>
              </el-table-column>
              <el-table-column label="判定" width="90">
                <template #default="{ row }">
                  <el-tag v-if="row.warn" type="danger" size="small">瓶颈</el-tag>
                  <el-tag v-else type="success" size="small">充裕</el-tag>
                </template>
              </el-table-column>
            </el-table>

            <template v-if="layout.bottlenecks.length">
              <h4 class="yc-sim__subsect">瓶颈网点</h4>
              <el-table :data="layout.bottlenecks" size="small" class="yc-sim__edges">
                <el-table-column label="网点" min-width="180" show-overflow-tooltip>
                  <template #default="{ row }">{{ siteName(row.site_code, row.site_name) }}</template>
                </el-table-column>
                <el-table-column prop="city" label="城市" width="100" />
                <el-table-column prop="capacity" label="能力（件/日）" width="120" align="right" />
                <el-table-column prop="demand" label="分到需求（件/日）" width="140" align="right" />
                <el-table-column label="负载" width="90" align="right">
                  <template #default="{ row }">{{ percentText(row.load_ratio) }}</template>
                </el-table-column>
              </el-table>
            </template>
          </template>

          <!-- 兜底：result 是合法 JSON 对象但结构对不上任何场景（库里被手改过），原样给出来 -->
          <template v-else>
            <p class="yc-sim__hint">
              结果结构对不上场景「{{ scenario ?? '未知' }}」，原始内容如下：
            </p>
            <pre class="yc-sim__json">{{ detail.result }}</pre>
          </template>
        </template>
      </div>

      <template #footer>
        <el-button
          v-if="detail && canAct(detail.status, 'edit')"
          v-perm="'nos:sim:create'"
          type="primary"
          @click="openEdit(detail)"
        >
          编辑参数
        </el-button>
        <el-button
          v-if="detail && canAct(detail.status, 'run')"
          v-perm="'nos:sim:run'"
          type="success"
          :loading="running"
          @click="run(detail)"
        >
          运行
        </el-button>
        <el-button @click="detailOpen = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.yc-sim {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 220px;
  }

  &__ref {
    width: 170px;
  }

  &__full {
    width: 100%;
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }

  &__hint {
    margin: var(--yc-space-1) 0 var(--yc-space-3);
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
    line-height: 1.7;

    &--inline {
      margin-left: var(--yc-space-3);
    }
  }

  &__facts {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
    gap: var(--yc-space-2) var(--yc-space-4);
    margin-bottom: var(--yc-space-4);
  }

  &__fact {
    font-size: var(--yc-font);

    span {
      margin-right: var(--yc-space-2);
      color: var(--yc-text-secondary);
      font-size: var(--yc-font-sm);
    }
  }

  &__sect {
    margin: var(--yc-space-4) 0 var(--yc-space-2);
    font-size: var(--yc-font);
    font-weight: 600;
  }

  &__subsect {
    margin: var(--yc-space-4) 0 var(--yc-space-2);
    font-size: var(--yc-font-sm);
    font-weight: 600;
  }

  &__json {
    margin: 0;
    padding: var(--yc-space-3);
    border-radius: var(--yc-radius, 6px);
    background: var(--yc-bg-subtle, #f7f8fa);
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: var(--yc-font-xs);
    line-height: 1.8;
    white-space: pre-wrap;
    word-break: break-all;
  }

  &__cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
    gap: var(--yc-space-3);
    margin-bottom: var(--yc-space-4);
  }

  &__card {
    text-align: center;
  }

  &__value {
    font-size: var(--yc-font-xl);
    font-weight: 600;

    &--good {
      color: var(--yc-success, #16a34a);
    }

    &--bad {
      color: var(--yc-danger, #dc2626);
    }
  }

  &__label {
    margin-top: var(--yc-space-1);
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
  }

  &__edges {
    width: 100%;
  }

  &__cityrow {
    display: grid;
    grid-template-columns: 1fr 200px auto;
    gap: var(--yc-space-2);
    align-items: center;
    margin-bottom: var(--yc-space-2);
  }

  &__citydemand {
    width: 200px;
  }
}
</style>
